<template>
    <Dialog 
        :visible="visible" 
        @update:visible="(val) => emit('update:visible', val)"
        modal 
        :header="mode === 'preview' ? 'Invoice Preview' : 'View Invoice'"
        class="w-[95svw] sm:w-[70vw] h-[90svh] sm:h-[90vh]"
    >
        <div v-if="invoice">
            <div class="flex justify-between items-center mb-4">
                <div>
                    <p>
                        <span v-if="mode === 'preview'">Invoice Preview for:</span>
                        <span v-else>Invoice:</span>
                        {{ invoice.invoiceNumber }}
                    </p>
                    <p>User: {{ invoice.userName }}</p>
                </div>
                <Button 
                    v-if="mode === 'view'"
                    icon="pi pi-download" 
                    @click="downloadPDF" 
                    :loading="isDownloading"
                    :disabled="!pdfUrl"
                    severity="secondary"
                    v-tooltip.bottom="'Download PDF'"
                />
            </div>
            <!-- Add loading state -->
            <div v-if="isLoading" class="flex justify-center items-center p-4">
                <ProgressSpinner />
            </div>
            <!-- Add error state -->
            <div v-if="error">{{ error }}</div>
            <!-- PDF viewer -->
            <div v-if="pdfUrl" class="pdf-container">
                <iframe 
                    :src="`${pdfUrl}#toolbar=0`"
                    width="100%" 
                    height="600px" 
                    frameborder="0"
                ></iframe>
            </div>

        </div>
    </Dialog>
</template>

<script setup>
import Dialog from 'primevue/dialog';
import { defineProps, defineEmits, onMounted, onUnmounted, ref, watch } from 'vue';
import InvoiceService from '@/services/InvoiceService';
import ProgressSpinner from 'primevue/progressspinner';
import Button from 'primevue/button';

const props = defineProps({
    visible: {
        type: Boolean,
        required: true
    },
    invoice: {
        type: Object,
        default: null
    },
    mode: {
        type: String,
        default: 'preview',
        validator: (value) => ['preview', 'view'].includes(value)
    }
});

const emit = defineEmits(['update:visible']);

const isLoading = ref(false);
const error = ref(null);
const pdfUrl = ref(null);
const isDownloading = ref(false);

const fetchPDF = async () => {
    if (!props.invoice?.id) return;
    
    try {
        isLoading.value = true;
        error.value = null;
        console.log('fetching pdf', props.mode);
        const response = props.mode === 'preview' 
            ? await InvoiceService.fetchInvoicePDFById(props.invoice.id)
            : await InvoiceService.fetchGeneratedInvoicePDFById(props.invoice.id);
            
        if (props.mode === 'preview') {
            // Handle blob response for preview
            const blob = new Blob([response.data], { type: 'application/pdf' });
            pdfUrl.value = URL.createObjectURL(blob);
        } else {
            const url = response.data.url;
            pdfUrl.value = url;
        }
        
    } catch (err) {
        console.error('Error fetching PDF:', err);
        error.value = `Failed to ${props.mode} PDF`;
    } finally {
        isLoading.value = false;
    }
};

// Watch for dialog visibility and invoice changes
watch(() => props.visible, (newVal) => {
    if (newVal && props.invoice) {
        fetchPDF();
    } else if (!newVal && pdfUrl.value) {
        if (props.mode === 'preview') {
            URL.revokeObjectURL(pdfUrl.value);
        }
        pdfUrl.value = null;
    }
});

// Keep cleanup in unmounted for preview mode
onUnmounted(() => {
    if (pdfUrl.value && props.mode === 'preview') {
        URL.revokeObjectURL(pdfUrl.value);
    }
    pdfUrl.value = null;
});

// Initial fetch if dialog is already visible
onMounted(() => {
    if (props.visible && props.invoice) {
        fetchPDF();
    }
});

const downloadPDF = async () => {
    if (!pdfUrl.value) return;
    
    try {
        isDownloading.value = true;
        
        // Use existing pdfUrl directly
        const pdfResponse = await fetch(pdfUrl.value);
        if (!pdfResponse.ok) throw new Error('Failed to fetch PDF');
        
        const blob = await pdfResponse.blob();
        const url = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = props.invoice.invoiceNumber || 'download.pdf';
        document.body.appendChild(link);
        link.click();
        
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading PDF:', error);
    } finally {
        isDownloading.value = false;
    }
};
</script>

<style scoped>
.pdf-container {
    margin-top: 1rem;
    border: 1px solid #ddd;
    border-radius: 4px;
}
</style> 